import type { FiniquitoInput, FiniquitoResult } from '../types';

function diffDays(a: Date, b: Date): number {
  return Math.floor((b.getTime() - a.getTime()) / 86400000);
}

function diffMonths(from: Date, to: Date): number {
  return (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
}

function diffYears(from: Date, to: Date): number {
  return Math.floor(diffMonths(from, to) / 12);
}

export function formatCLP(n: number): string {
  return '$' + Math.round(n).toLocaleString('es-CL');
}

/**
 * Feriados fijos chilenos (mes 1-12, día).
 * Se agregan al período laboral para descontarlos del cómputo de días hábiles.
 */
const FERIADOS_FIJOS: { mes: number; dia: number }[] = [
  { mes: 1,  dia: 1  }, // Año Nuevo
  { mes: 5,  dia: 1  }, // Día del Trabajo
  { mes: 5,  dia: 21 }, // Glorias Navales
  { mes: 6,  dia: 20 }, // Día de los Pueblos Indígenas (movible aprox — usamos 20 jun)
  { mes: 6,  dia: 29 }, // San Pedro y San Pablo
  { mes: 7,  dia: 16 }, // Virgen del Carmen
  { mes: 8,  dia: 15 }, // Asunción de la Virgen
  { mes: 9,  dia: 18 }, // Independencia
  { mes: 9,  dia: 19 }, // Glorias del Ejército
  { mes: 10, dia: 12 }, // Encuentro de dos mundos
  { mes: 10, dia: 31 }, // Día de las Iglesias Evangélicas
  { mes: 11, dia: 1  }, // Todos los Santos
  { mes: 12, dia: 8  }, // Inmaculada Concepción
  { mes: 12, dia: 25 }, // Navidad
];

/**
 * Cuenta los días inhábiles (sábados, domingos y festivos) entre dos fechas.
 * Retorna { sabDom, festivos, total }.
 */
function contarInhabiles(from: Date, to: Date): { sabDom: number; festivos: number; total: number } {
  let sabDom = 0;
  let festivos = 0;
  const cur = new Date(from);

  while (cur <= to) {
    const dow = cur.getDay(); // 0=Dom, 6=Sáb
    const mes = cur.getMonth() + 1;
    const dia = cur.getDate();

    const esFinde = dow === 0 || dow === 6;
    const esFestivo = !esFinde && FERIADOS_FIJOS.some(f => f.mes === mes && f.dia === dia);

    if (esFinde) sabDom++;
    if (esFestivo) festivos++;

    cur.setDate(cur.getDate() + 1);
  }
  return { sabDom, festivos, total: sabDom + festivos };
}

/**
 * Dado un número de días hábiles (lun-vie) pendientes, estima cuántos
 * días inhábiles (sáb, dom, festivos) se suman.
 * Usamos proporción: 5 hábiles → 2 sáb/dom → +40 %
 * y estimamos festivos ≈ 14 festivos/año ÷ 261 días hábiles ≈ 5.4 % extra.
 */
function inhabilesPorHabiles(habiles: number): { sabDom: number; festivos: number; total: number } {
  const sabDom = habiles * 2 / 5;          // 2 días finde por cada 5 hábiles
  const festivos = habiles * 14 / 261;     // ~14 festivos / 261 días hábiles anuales
  return { sabDom, festivos, total: sabDom + festivos };
}

export function calcularFiniquito(input: FiniquitoInput): FiniquitoResult | null {
  const { causal, fIngreso, fAviso, fTermino, sueldoBase, vacUsadas } = input;

  if (!causal || !fIngreso || !fTermino || !sueldoBase) return null;

  const ingreso = new Date(fIngreso + 'T00:00:00');
  const termino = new Date(fTermino + 'T00:00:00');
  const aviso   = fAviso ? new Date(fAviso + 'T00:00:00') : null;

  const meses          = diffMonths(ingreso, termino);
  const anios          = diffYears(ingreso, termino);
  const aniosParaCalculo = Math.min(anios, 11);
  const sueldoDia      = sueldoBase / 30;

  // Días trabajados en el mes de término (proporcional)
  const diaDelMes        = termino.getDate();
  const sueldoProporcional = sueldoDia * diaDelMes;

  // Vacaciones: 1.25 días hábiles (lun-vie) por mes trabajado
  const diasVacGanados   = 1.25 * meses;
  const diasVacHabiles   = Math.max(0, diasVacGanados - vacUsadas);

  // Sumar inhábiles (sáb, dom, festivos) que caen dentro del período vacacional.
  // Si tenemos las fechas exactas calculamos con el calendario real;
  // de lo contrario usamos la estimación proporcional.
  let inhabiles: { sabDom: number; festivos: number; total: number };
  if (fIngreso && fTermino && diasVacHabiles > 0) {
    // Usamos estimación proporcional (no sabemos cuándo se tomarán las vacaciones)
    inhabiles = inhabilesPorHabiles(diasVacHabiles);
  } else {
    inhabiles = { sabDom: 0, festivos: 0, total: 0 };
  }

  const diasVacTotal   = diasVacHabiles + inhabiles.total;   // hábiles + inhábiles
  const vacaciones     = diasVacTotal * sueldoDia;

  let indemnizacionAnios  = 0;
  let indemnizacionAviso  = 0;
  let indemnizacionFaena  = 0;

  const causalLabels: Record<string, string> = {
    '161':   'Art. 161 — Necesidades de la empresa',
    '159n2': 'Art. 159 N°2 — Renuncia voluntaria',
    '159n4': 'Art. 159 N°4 — Vencimiento plazo fijo',
    '159n5': 'Art. 159 N°5 — Conclusión de obra o faena',
    '160':   'Art. 160 — Despido por falta grave',
  };

  switch (causal) {
    case '161': {
      indemnizacionAnios = aniosParaCalculo * sueldoBase;
      if (aviso) {
        const diasAviso = diffDays(aviso, termino);
        if (diasAviso < 30) indemnizacionAviso = sueldoBase;
      } else {
        indemnizacionAviso = sueldoBase;
      }
      break;
    }
    case '159n2':
    case '159n4':
    case '160':
      break;
    case '159n5': {
      indemnizacionFaena = meses * 2.5 * sueldoDia;
      break;
    }
  }

  const sabDomRound  = Math.round(inhabiles.sabDom);
  const festivosRound = Math.round(inhabiles.festivos);
  const vacSublabel  = diasVacHabiles > 0
    ? `${diasVacHabiles.toFixed(1)} días hábiles + ${sabDomRound} sáb/dom + ${festivosRound} festivos = ${Math.round(diasVacTotal)} días`
    : '0 días pendientes';

  const desglose: { label: string; sublabel?: string; value: number }[] = [
    { label: 'Sueldo proporcional', sublabel: `${diaDelMes} días del mes`, value: sueldoProporcional },
    { label: 'Vacaciones proporcionales', sublabel: vacSublabel, value: vacaciones },
  ];

  if (causal === '161') {
    desglose.push({
      label: 'Indemnización por años',
      sublabel: `${aniosParaCalculo} año${aniosParaCalculo !== 1 ? 's' : ''} (tope 11)`,
      value: indemnizacionAnios,
    });
    desglose.push({
      label: 'Indemnización sustitutiva aviso',
      sublabel: indemnizacionAviso > 0 ? 'Sin aviso previo de 30 días' : 'Aviso dado oportunamente',
      value: indemnizacionAviso,
    });
  }

  if (causal === '159n5') {
    desglose.push({
      label: 'Indemnización obra/faena',
      sublabel: `${meses} meses × 2.5 días`,
      value: indemnizacionFaena,
    });
  }

  const total = desglose.reduce((s, d) => s + d.value, 0);

  const aniosLabel = anios > 0
    ? `${anios} año${anios !== 1 ? 's' : ''} y ${meses % 12} mes${meses % 12 !== 1 ? 'es' : ''} de antigüedad`
    : `${meses} mes${meses !== 1 ? 'es' : ''} de antigüedad`;

  return {
    causalLabel: causalLabels[causal] || causal,
    antiguedad: aniosLabel,
    total,
    desglose,
  };
}
